export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Record<string, unknown> = {},
  children: (Node | string | null | undefined)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value == null || value === false) continue;
    if (key === 'className') {
      node.className = String(value);
    } else if (key === 'text') {
      node.textContent = String(value);
    } else if (key.startsWith('on') && typeof value === 'function') {
      const event = key.slice(2).toLowerCase();
      node.addEventListener(event, value as EventListener);
    } else if (key === 'dataset' && typeof value === 'object') {
      for (const [dk, dv] of Object.entries(value as Record<string, string>)) {
        node.dataset[dk] = dv;
      }
    } else if (typeof value === 'boolean') {
      if (value) node.setAttribute(key, '');
    } else {
      node.setAttribute(key, String(value));
    }
  }
  for (const child of children) {
    if (child == null) continue;
    node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

export function clear(node: HTMLElement) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function appendAll(parent: ParentNode, ...nodes: (Node | null | undefined)[]) {
  for (const n of nodes) {
    if (n) parent.appendChild(n);
  }
}

export function logoSvg(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '22');
  svg.setAttribute('height', '22');
  svg.setAttribute('viewBox', '0 0 32 32');
  svg.setAttribute('fill', 'none');
  svg.innerHTML =
    '<circle cx="14" cy="14" r="7" stroke="currentColor" stroke-width="2.2"/>' +
    '<path d="M19.5 19.5L26 26" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>';
  return svg;
}

export function renderShell(
  mount: HTMLElement,
  active: 'runs' | 'new' | 'clients' | 'subscriptions',
  content: HTMLElement,
  pendingDeliveries = 0,
) {
  clear(mount);
  const shell = el('div', { className: 'shell' });
  const subsLabel =
    pendingDeliveries > 0
      ? `Subscriptions (${pendingDeliveries})`
      : 'Subscriptions';
  const sidebar = el('aside', { className: 'sidebar' }, [
    el('div', { className: 'brand' }, [logoSvg(), document.createTextNode('Prospector')]),
    el('nav', { className: 'nav' }, [
      el('a', {
        href: '#/runs',
        className: active === 'runs' ? 'active' : '',
        text: 'Runs',
      }),
      el('a', {
        href: '#/runs/new',
        className: active === 'new' ? 'active' : '',
        text: 'New Run',
      }),
      el('a', {
        href: '#/subscriptions',
        className: active === 'subscriptions' ? 'active' : '',
        text: subsLabel,
      }),
      el('a', {
        href: '#/clients',
        className: active === 'clients' ? 'active' : '',
        text: 'Clients',
      }),
    ]),
    el('div', { className: 'sidebar-foot', text: 'v1 · admin bypass' }),
  ]);
  const main = el('main', { className: 'main' }, [content]);
  shell.appendChild(sidebar);
  shell.appendChild(main);
  mount.appendChild(shell);
}

export function errorBanner(message: string, onRetry?: () => void): HTMLElement {
  const wrap = el('div', { className: 'banner-error' });
  wrap.appendChild(el('div', { text: message }));
  if (onRetry) {
    appendAll(wrap, el('button', {
        className: 'btn',
        type: 'button',
        text: 'Retry',
        onClick: onRetry,
      }),
    );
  }
  return wrap;
}

export function skeletonTable(rows = 6): HTMLElement {
  const wrap = el('div', { className: 'table-wrap' });
  const table = el('table', { className: 'data' });
  const body = el('tbody');
  for (let i = 0; i < rows; i++) {
    const tr = el('tr');
    for (let j = 0; j < 5; j++) {
      tr.appendChild(el('td', {}, [el('div', { className: 'skeleton' })]));
    }
    body.appendChild(tr);
  }
  table.appendChild(body);
  wrap.appendChild(table);
  return wrap;
}
